import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader, UserNameCell } from '@/components/core';
import { useIsMobile } from '@/hooks/use-media-query';
import { FULFILMENT_STATUS_OPTIONS } from '@/modules/service-desk/shared/application-utils';
import {
  actionsColumn,
  appliedDateColumn,
  departmentColumn,
  DepartmentOption,
  NAME_ON_CARD,
  optionsFilter,
  statusColumn,
  textColumn,
  withCard,
} from '@/modules/service-desk/shared/components/column-helpers';
import { StatusSelectCell } from '@/modules/service-desk/shared/components/status-select-cell';
import {
  canEditIdCard,
  ID_CARD_APPLICATION_TYPES,
  isIdCardStatusLocked,
  PAYMENT_METHODS,
} from '../../utils/id-card-rules';
import { IdCardApplication, IdCardStatus } from '../../types/id-card.types';

type Translate = (key: string) => string;

const STATUSES = FULFILMENT_STATUS_OPTIONS.map((option) => option.value);

const typeLabel = (t: Translate, type: IdCardApplication['ApplicationType']) => {
  const match = ID_CARD_APPLICATION_TYPES.find((option) => option.value === type);
  return match ? t(match.labelKey) : type;
};

const applicationTypeColumn = (t: Translate): ColumnDef<IdCardApplication> =>
  textColumn<IdCardApplication>(
    t,
    'ApplicationType',
    'APPLICATION_TYPE',
    (row) => typeLabel(t, row.ApplicationType),
    { filter: optionsFilter(t, ID_CARD_APPLICATION_TYPES) }
  );

/** The name with the email under it; on a phone card the name alone (the email has its own value). */
const NameCell = ({ application }: { application: IdCardApplication }) =>
  useIsMobile() ? (
    <>{application.Name || '-'}</>
  ) : (
    <UserNameCell name={application.Name} email={application.Email} />
  );

/** Name with the email under it, as in the legacy lists; sorted and filtered by name. */
const nameColumn = (t: Translate): ColumnDef<IdCardApplication> => ({
  id: 'Name',
  meta: { filter: { type: 'text' }, ...NAME_ON_CARD },
  header: ({ column }) => <DataTableColumnHeader column={column} title={t('NAME')} />,
  accessorFn: (row) => row.Name || '-',
  cell: ({ row }) => <NameCell application={row.original} />,
});

const mobileColumn = (t: Translate) =>
  textColumn<IdCardApplication>(t, 'MobileNumber', 'MOBILE_NUMBER', (row) => row.MobileNumber);

/**
 * Legacy "Payment info": the method (stored and shown as its literal name) with its reference under
 * it; filtered by method.
 */
const paymentInfoColumn = (t: Translate): ColumnDef<IdCardApplication> => ({
  ...textColumn<IdCardApplication>(t, 'PaymentMethod', 'PAYMENT_INFO', (row) => row.PaymentMethod, {
    filter: {
      type: 'options',
      options: PAYMENT_METHODS.map((method) => ({ value: method, label: method })),
    },
  }),
  cell: ({ row }) =>
    row.original.PaymentMethod ? (
      <div className="flex flex-col">
        <span>{row.original.PaymentMethod}</span>
        {row.original.PaymentReference && (
          <span className="text-xs text-muted-foreground">{row.original.PaymentReference}</span>
        )}
      </div>
    ) : (
      '-'
    ),
});

/** A "View" link to a stored photo/receipt, or a dash when none was uploaded. */
const fileLinkColumn = (
  t: Translate,
  id: string,
  titleKey: string,
  fileIdOf: (row: IdCardApplication) => string | undefined,
  fileUrls: Record<string, string>
): ColumnDef<IdCardApplication> => ({
  id,
  header: ({ column }) => <DataTableColumnHeader column={column} title={t(titleKey)} />,
  enableSorting: false,
  cell: ({ row }) => {
    const fileId = fileIdOf(row.original);
    const url = fileId ? fileUrls[fileId] : undefined;
    return url ? (
      <a href={url} target="_blank" rel="noreferrer" className="text-primary underline">
        {t('VIEW')}
      </a>
    ) : (
      '-'
    );
  },
});

interface MyColumnsProps {
  t: Translate;
  /** Every department, to name the applicant's on the phone card. */
  departments: readonly DepartmentOption[];
  fileUrls: Record<string, string>;
  canApply: boolean;
  canWithdraw: boolean;
  onEdit: (application: IdCardApplication) => void;
  onWithdraw: (application: IdCardApplication) => void;
}

export const createMyIdCardColumns = ({
  t,
  departments,
  fileUrls,
  canApply,
  canWithdraw,
  onEdit,
  onWithdraw,
}: MyColumnsProps): ColumnDef<IdCardApplication>[] => [
  // Card: the application type is the title and the actions menu the top right. The body runs
  // Request Date | Status, Name | Email, Designation | Department, Employee ID | Mobile,
  // Blood Group | Payment Info, with the photo and payment receipt in the footer.
  withCard(appliedDateColumn<IdCardApplication>(t, 'REQUEST_DATE'), {
    mobile: 'body',
    mobileOrder: 0,
  }),
  withCard(
    textColumn<IdCardApplication>(t, 'EmployeeId', 'EMPLOYEE_ID', (row) => row.EmployeeId),
    { mobile: 'body', mobileOrder: 6 }
  ),
  withCard(nameColumn(t), { mobile: 'body', mobileOrder: 2 }),
  // On the card only: the table shows the email under the name.
  withCard(
    textColumn<IdCardApplication>(t, 'Email', 'EMAIL', (row) => row.Email),
    { mobile: 'body', mobileOrder: 3, cardOnly: true }
  ),
  withCard(
    textColumn<IdCardApplication>(t, 'Designation', 'DESIGNATION', (row) => row.Designation),
    { mobile: 'body', mobileOrder: 4 }
  ),
  // On the card only: the table of one's own applications has no department column.
  withCard(departmentColumn<IdCardApplication>(t, departments), {
    mobile: 'body',
    mobileOrder: 5,
    cardOnly: true,
  }),
  withCard(mobileColumn(t), { mobile: 'body', mobileOrder: 7 }),
  withCard(
    textColumn<IdCardApplication>(t, 'BloodGroup', 'BLOOD_GROUP', (row) => row.BloodGroup),
    { mobile: 'body', mobileOrder: 8 }
  ),
  withCard(applicationTypeColumn(t), { mobile: 'title' }),
  withCard(paymentInfoColumn(t), { mobile: 'body', mobileOrder: 9 }),
  withCard(
    fileLinkColumn(t, 'Photo', 'ATTACHED_PHOTO', (row) => row.PhotoId, fileUrls),
    {
      mobile: 'footer',
    }
  ),
  withCard(
    fileLinkColumn(t, 'Receipt', 'PAYMENT_RECEIPT', (row) => row.PaymentReferenceId, fileUrls),
    { mobile: 'footer' }
  ),
  withCard(statusColumn<IdCardApplication>(t, STATUSES), { mobile: 'body', mobileOrder: 1 }),
  actionsColumn<IdCardApplication>(t, (application) => [
    {
      label: t('EDIT'),
      onSelect: () => onEdit(application),
      hidden: !canApply || !canEditIdCard(application),
    },
    {
      label: t('WITHDRAW_ID_CARD'),
      onSelect: () => onWithdraw(application),
      hidden: !canWithdraw || application.Status !== 'Pending',
      destructive: true,
    },
  ]),
];

interface RequestColumnsProps {
  t: Translate;
  /** The departments the reviewer may see, for the Department column and its filter. */
  departments: readonly DepartmentOption[];
  canUpdateStatus: boolean;
  fileUrls: Record<string, string>;
  /** The application whose status change is being saved; its select is disabled meanwhile. */
  savingId?: string;
  onChangeStatus: (application: IdCardApplication, status: IdCardStatus) => void;
}

export const createIdCardRequestColumns = ({
  t,
  departments,
  canUpdateStatus,
  fileUrls,
  savingId,
  onChangeStatus,
}: RequestColumnsProps): ColumnDef<IdCardApplication>[] => [
  // Card: the request date is the title, the status select the top right. The body runs
  // Name | Email, Designation | Department, Employee ID | Mobile, Blood Group | Application Type,
  // Payment Info | Payment Receipt, with the photo in the footer.
  withCard(appliedDateColumn<IdCardApplication>(t, 'REQUESTED_ON'), { mobile: 'title' }),
  withCard(
    textColumn<IdCardApplication>(t, 'EmployeeId', 'EMPLOYEE_ID', (row) => row.EmployeeId),
    { mobile: 'body', mobileOrder: 4 }
  ),
  withCard(nameColumn(t), { mobile: 'body', mobileOrder: 0 }),
  // On the card only: the table shows the email under the name.
  withCard(
    textColumn<IdCardApplication>(t, 'Email', 'EMAIL', (row) => row.Email),
    { mobile: 'body', mobileOrder: 1, cardOnly: true }
  ),
  withCard(
    textColumn<IdCardApplication>(t, 'Designation', 'DESIGNATION', (row) => row.Designation),
    { mobile: 'body', mobileOrder: 2 }
  ),
  withCard(departmentColumn<IdCardApplication>(t, departments), {
    mobile: 'body',
    mobileOrder: 3,
  }),
  withCard(mobileColumn(t), { mobile: 'body', mobileOrder: 5 }),
  withCard(
    textColumn<IdCardApplication>(t, 'BloodGroup', 'BLOOD_GROUP', (row) => row.BloodGroup),
    { mobile: 'body', mobileOrder: 6 }
  ),
  withCard(applicationTypeColumn(t), { mobile: 'body', mobileOrder: 7 }),
  withCard(paymentInfoColumn(t), { mobile: 'body', mobileOrder: 8 }),
  withCard(
    fileLinkColumn(t, 'Photo', 'ATTACHED_PHOTO', (row) => row.PhotoId, fileUrls),
    {
      mobile: 'footer',
    }
  ),
  withCard(
    fileLinkColumn(t, 'Receipt', 'PAYMENT_RECEIPT', (row) => row.PaymentReferenceId, fileUrls),
    { mobile: 'body', mobileOrder: 9 }
  ),
  statusColumn<IdCardApplication>(t, STATUSES, ({ row }) => (
    <StatusSelectCell
      status={row.original.Status}
      options={FULFILMENT_STATUS_OPTIONS}
      locked={!canUpdateStatus || isIdCardStatusLocked(row.original)}
      disabled={row.original.ItemId === savingId}
      onChange={(status) => onChangeStatus(row.original, status as IdCardStatus)}
    />
  )),
];
